import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { createSignedQRUrl } from '../../lib/storage/payment-qr';
import { supabase, getAccessToken } from '../lib/supabase';

interface PaymentQRUploadProps {
  currentQRPath: string | null;
  onUploadSuccess: (storagePath: string) => void;
  onDeleteSuccess: () => void;
  disabled?: boolean;
}

export function PaymentQRUpload({
  currentQRPath,
  onUploadSuccess,
  onDeleteSuccess,
  disabled = false,
}: PaymentQRUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load preview URL when component mounts or QR path changes
  React.useEffect(() => {
    async function loadPreview() {
      if (currentQRPath) {
        const signedUrl = await createSignedQRUrl(supabase, currentQRPath);
        setPreviewUrl(signedUrl);
      } else {
        setPreviewUrl(null);
      }
    }
    loadPreview();
  }, [currentQRPath]);

  const validateFile = (file: File): string | null => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const maxSize = 2 * 1024 * 1024; // 2MB

    if (!allowedTypes.includes(file.type)) {
      return 'Invalid file type. Please upload a JPG, PNG, or WebP image.';
    }

    if (file.size > maxSize) {
      return `File size exceeds 2MB limit (${(file.size / 1024 / 1024).toFixed(2)}MB)`;
    }

    return null;
  };

  const handleFileSelect = async (file: File) => {
    setError(null);

    // Validate file
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setUploading(true);

      // Get access token for authentication
      const accessToken = await getAccessToken();

      if (!accessToken) {
        throw new Error('You must be logged in to upload files');
      }

      // Create FormData and upload
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/profile/payment-qr', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload file');
      }

      const data = await response.json();

      // Update preview with local file
      const localUrl = URL.createObjectURL(file);
      setPreviewUrl(localUrl);

      onUploadSuccess(data.storagePath);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!currentQRPath) return;

    try {
      setDeleting(true);
      setError(null);

      // Get access token for authentication
      const accessToken = await getAccessToken();

      if (!accessToken) {
        throw new Error('You must be logged in to delete files');
      }

      const response = await fetch('/api/profile/payment-qr', {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete file');
      }

      setPreviewUrl(null);
      onDeleteSuccess();
    } catch (err) {
      console.error('Delete error:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete file');
    } finally {
      setDeleting(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="space-y-3">
      {/* Upload/Preview Area */}
      {previewUrl ? (
        <div className="relative">
          <div className="border-2 border-gray-200 rounded-xl overflow-hidden bg-white">
            <img
              src={previewUrl}
              alt="Payment QR Code"
              className="w-full h-auto max-h-64 object-contain"
            />
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="absolute top-2 right-2 p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-lg transition-colors disabled:opacity-50"
            >
              {deleting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <X className="w-4 h-4" />
              )}
            </button>
          )}
        </div>
      ) : (
        <div
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
            ${isDragging ? 'border-[#5B8E87] bg-[#5B8E87]/5' : 'border-gray-300 hover:border-[#5B8E87]'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            ${uploading ? 'pointer-events-none opacity-50' : ''}
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileInputChange}
            disabled={disabled || uploading}
            className="hidden"
          />

          {uploading ? (
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 border-4 border-[#5B8E87] border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-600">Uploading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              {isDragging ? (
                <ImageIcon className="w-12 h-12 text-[#5B8E87]" />
              ) : (
                <Upload className="w-12 h-12 text-gray-400" />
              )}
              <div>
                <p className="text-sm font-medium text-[#212121] mb-1">
                  {isDragging ? 'Drop to upload' : 'Click to upload or drag and drop'}
                </p>
                <p className="text-xs text-gray-500">
                  JPG, PNG or WebP (max 2MB)
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2">
          <span className="text-red-600 text-sm">⚠️</span>
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Helper Text */}
      <p className="text-xs text-gray-500">
        Shared with clients when payment method is UPI
      </p>
    </div>
  );
}
